import { getFormattedDate } from "src/utils/getDates";
import { CreateFreightClaimInitialState, FcAttachement } from "../interface";
import { getItem, roundNumber } from "src/utils/genericFunctions";
import {
  freightClaimPoItemMapper,
  mapDamagedQuantityWithReceived
} from "src/utils/freightClaimUtils";
import convertToCurrency from "src/utils/convertToCurrency";
import stringToNumber from "src/utils/stringToNumber";
import { ApiStatus } from "src/config/enums/genericEnums";
import {
  IGetStoreCreditDetails,
  isValidQuantity,
  IValidQtyValue,
  mapPoItemsWithStoreCreditDetails
} from "src/utils/storeCreditUtils";

export const initialState: CreateFreightClaimInitialState = {
  poAdditionalDetails: {},
  poItems: null,
  poItemsCopy: [],
  searchQuery: "",
  searchResults: [],
  blockNavigation: true,
  wareHouse: "",
  poBasicData: {
    poType: "Freight Claim",
    supplier: "",
    effectiveDate: getFormattedDate(new Date(), "MM/DD/YYYY"),
    createdBy: "",
    purchasingOrganization: {
      name: "",
      responseCode: ""
    },
    inPalletReceivingProgram: false
  },
  reasonForClaim: "",
  notes: "",
  attachments: [],
  createPOLoading: false,
  getPOItemsStatus: "idle",
  getPOTransferStatus: "pending",
  uploadAttachmentLoading: false,
  searchProductsLoading: false,
  margin: 0,
  totalCost: 0,
  deletedAtttachmentPaths: [],
  totalTransfer: 0,
  overShortDocId: "",
  getStoreCreditDetailsStatus: "pending",
  getStoreCreditDetails: []
};

export const calCulateAdjustedExtendedRetail = (quantity: any, retail: any) => {
  const result = Number(quantity || 0) * stringToNumber(retail || 0);
  return result > 0 ? Number(`-${result}`) : result;
};

export const handleEditDamagedQuantity = (
  state: CreateFreightClaimInitialState,
  action: any
) => {
  const { uniqueId, quantity } = action.payload;
  const value = getItem(state.poItemsCopy, uniqueId);

  /* logic for blocking user to enter quantity limit to total received */

  if (
    value &&
    isValidQuantity(
      quantity.damagedQuantityCases as string,
      quantity.damagedQuantityEaches as string,
      value as unknown as IValidQtyValue,
      "FC"
    )
  ) {
    return;
  }

  let sum = 0;
  let transferCost = 0;
  let margin = 0;
  state.poItems.forEach((poItem: any) => {
    if (poItem.uniqueId === uniqueId) {
      poItem.damagedQuantityCases = quantity.damagedQuantityCases || "0";
      poItem.damagedQuantityEaches = quantity.damagedQuantityEaches || "0";

      poItem.damagedQuantity =
        +poItem.damagedQuantityCases * +poItem.pack +
        +poItem.damagedQuantityEaches +
        "";

      poItem.netPrice = convertToCurrency(
        "$",
        +poItem.damagedQuantity * stringToNumber(poItem?.transferCost || 0)
      );
      poItem.adjustedExtendedRetail = calCulateAdjustedExtendedRetail(
        poItem.damagedQuantity,
        poItem?.retail
      );
    }
    sum += calCulateAdjustedExtendedRetail(
      poItem.damagedQuantity,
      poItem?.retail
    );
    transferCost +=
      Number(-poItem?.damagedQuantity || 0) *
      stringToNumber(poItem?.transferCost || 0);
  });

  margin = sum ? ((sum - transferCost) / sum) * 100 : 0;
  state.poItemsCopy = [...state.poItems];
  state.totalCost = roundNumber(sum);
  state.margin = roundNumber(margin);
  state.totalTransfer = roundNumber(transferCost);
};

export const handleAddAttachment = (
  state: CreateFreightClaimInitialState,
  action: {
    payload: FcAttachement;
  }
) => {
  state.attachments.push(action.payload);
};

export const handleRemoveAttachment = (
  state: CreateFreightClaimInitialState,
  action: { payload: number }
) => {
  const filterAttachments: {
    fileName: string;
    fileURL: string;
    fileType: string;
  }[] = state.attachments.filter(
    (_item: any, itemIndex: number) => itemIndex !== action?.payload
  );
  state.deletedAtttachmentPaths = [
    ...state.deletedAtttachmentPaths,
    state.attachments[action?.payload].fileURL
  ];
  state.attachments = filterAttachments;
};

export const handleReset = () => {
  return initialState;
};

export const handleUpdateNavigationBlocking = (
  state: CreateFreightClaimInitialState,
  action: any
) => {
  state.blockNavigation = action.payload;
};

export const handleUpdatePOBasicData = (
  state: CreateFreightClaimInitialState,
  action: any
) => {
  const data = action.payload;
  state.poBasicData = data;
};

export const handleUpdateNotes = (
  state: CreateFreightClaimInitialState,
  action: any
) => {
  const notes = action.payload;
  state.notes = notes;
};

export const handleUpdateReasonForClaim = (
  state: CreateFreightClaimInitialState,
  action: any
) => {
  const reasonForClaim = action.payload;
  state.reasonForClaim = reasonForClaim;
};

export const handleUploadFreightClaimAttachmentsPending = (
  state: CreateFreightClaimInitialState
) => {
  state.uploadAttachmentLoading = true;
};

export const handleUploadFreightClaimAttachmentsFulfilled = (
  state: CreateFreightClaimInitialState
) => {
  state.uploadAttachmentLoading = false;
};

export const handleUploadFreightClaimAttachmentsRejected = (
  state: CreateFreightClaimInitialState
) => {
  state.uploadAttachmentLoading = false;
};

export const handleCreateFreightClaimPoPending = (
  state: CreateFreightClaimInitialState
) => {
  state.createPOLoading = true;
};

export const handleCreateFreightClaimPoFulfilled = (
  state: CreateFreightClaimInitialState
) => {
  state.createPOLoading = false;
};

export const handleCreateFreightClaimPoRejected = (
  state: CreateFreightClaimInitialState
) => {
  state.createPOLoading = false;
};

export const handleGetTransferPOProductsForFcPending = (
  state: CreateFreightClaimInitialState
) => {
  state.getPOTransferStatus = ApiStatus.PENDING;
};

export const handleGetStoreCreditDetailsForFcPending = (
  state: CreateFreightClaimInitialState
) => {
  state.getStoreCreditDetailsStatus = ApiStatus.PENDING;
};

const getPoAdditionalDetail = (data: any) => ({
  docId: data?.docId,
  isStoreCredit: true,
  invoiceLabel: data?.invoiceLabel,
  wareHouse: data?.wareHouse,
  referenceTransferReceivedDate: data?.receivedDate
});

export const handleGetTransferPOProductsForFcFulfilled = (
  state: CreateFreightClaimInitialState,
  { payload }: { payload: any }
) => {
  if (payload?.data) {
    const mappedFCWithStoreCreditDetails = mapPoItemsWithStoreCreditDetails(
      payload?.data?.poItems,
      state,
      "FC"
    );
    const freightClaimMappedItem = mappedFCWithStoreCreditDetails.map((item) =>
      freightClaimPoItemMapper(item)
    );
    state.poItems = freightClaimMappedItem;
    state.poItemsCopy = freightClaimMappedItem;
    state.poAdditionalDetails = getPoAdditionalDetail(payload.data);
    state.poBasicData.createdBy = payload.data?.createdByUser;
    state.poBasicData.inPalletReceivingProgram =
      payload.data.inPalletReceivingProgram;
    state.poBasicData.purchasingOrganization =
      payload.data?.purchasingOrganization;
    state.poBasicData.supplier = payload.data?.invoiceLabel;
    state.getPOTransferStatus = ApiStatus.IDLE;
  } else {
    state.getPOTransferStatus = ApiStatus.ERROR;
  }
};

export const handleGetStoreCreditDetailsForFcFulfilled = (
  state: CreateFreightClaimInitialState,
  {
    payload
  }: { payload: { data: { docId: string; poItems: IGetStoreCreditDetails[] } } }
) => {
  if (payload?.data) {
    state.overShortDocId = payload.data?.docId;
    state.getStoreCreditDetails = payload.data?.poItems;
    state.getStoreCreditDetailsStatus = ApiStatus.IDLE;
  } else {
    state.getStoreCreditDetailsStatus = ApiStatus.ERROR;
  }
};

export const handleGetTransferPOProductsForFcRejected = (
  state: CreateFreightClaimInitialState
) => {
  state.getPOTransferStatus = ApiStatus.ERROR;
};

export const handleGetStoreCreditDetailsForFcRejected = (
  state: CreateFreightClaimInitialState
) => {
  state.getStoreCreditDetailsStatus = ApiStatus.ERROR;
};

export const handleSelectAllPalletItems = (
  state: any,
  action: {
    payload: { palletId: string; selectedState: boolean; headerId: string };
  }
) => {
  const { palletId, selectedState } = action.payload;

  let sum = 0;
  let transferCost = 0;
  let margin = 0;
  const updatedFcItems = state.poItems.map((poItem: any) => {
    if (poItem.palletId !== palletId) {
      transferCost +=
        -Number(poItem?.damagedQuantity || "0") *
        stringToNumber(poItem?.transferCost || "0");
      sum += -(
        Number(poItem?.damagedQuantity || 0) *
        Number(poItem?.retail?.slice(1)?.replace(/,/g, "") || 0)
      );
      return poItem;
    }

    mapDamagedQuantityWithReceived(poItem, selectedState);

    transferCost +=
      -Number(poItem?.damagedQuantity || "0") *
      stringToNumber(poItem?.transferCost || "0");
    sum += -(
      Number(poItem?.damagedQuantity || 0) *
      Number(poItem?.retail?.slice(1)?.replace(/,/g, "") || 0)
    );

    return poItem;
  });

  state.poItemsCopy = updatedFcItems;
  state.poItems = [...updatedFcItems];

  margin = sum ? ((sum - transferCost) / sum) * 100 : 0;
  state.totalCost = roundNumber(sum);
  state.margin = +transferCost === 0 ? 0 : roundNumber(margin);
  state.totalTransfer = roundNumber(transferCost);
};
